import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { easing } from "@/lib/animations";
import { useCart } from "@/context/CartContext";
import { BlinkingDot, MonoLabel, FieldButton } from "@/components/proof/primitives";
import {
    PRODUCTS,
    formatPrice,
    getProductBySlug,
    getProductByN,
} from "@/data/products";

/**
 * Product Detail — full-bleed gallery on the left, sticky purchase column on the right.
 * Below: FIELD NOTES (lore) + PAIR WITH (related artifacts).
 */
export default function Product() {
    const { slug } = useParams();
    const product = getProductBySlug(slug);
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [size, setSize] = useState(null);
    const [galleryIdx, setGalleryIdx] = useState(0);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        setGalleryIdx(0);
        setAdded(false);
        setSize(product?.sizes?.[Math.floor((product?.sizes?.length || 1) / 2)] || null);
    }, [product]);

    const pairWithItems = useMemo(() => {
        if (!product) return [];
        return (product.pairWith || [])
            .map((n) => getProductByN(n))
            .filter(Boolean);
    }, [product]);

    if (!product) {
        return (
            <section
                style={{
                    minHeight: "100vh",
                    background: "var(--ink)",
                    color: "var(--on-ink)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "120px 24px",
                    flexDirection: "column",
                    gap: 22,
                }}
                data-testid="product-notfound"
            >
                <MonoLabel tone="mute">Signal lost</MonoLabel>
                <h1
                    style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(36px, 6vw, 72px)",
                        letterSpacing: "-0.04em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Artifact not found.
                </h1>
                <FieldButton
                    to="/shop"
                    data-testid="product-notfound-back"
                    style={{ marginTop: 16 }}
                >
                    Return to Lookbook
                </FieldButton>
            </section>
        );
    }

    const handleAdd = () => {
        if (!size) return;
        addItem({
            id: product.n,
            slug: product.slug,
            name: product.name,
            priceEUR: product.priceEUR,
            size,
            img: product.cover,
        });
        setAdded(true);
        setTimeout(() => setAdded(false), 1600);
    };

    const handleBuyNow = () => {
        if (!size) return;
        addItem({
            id: product.n,
            slug: product.slug,
            name: product.name,
            priceEUR: product.priceEUR,
            size,
            img: product.cover,
        });
        navigate("/checkout");
    };

    return (
        <section
            style={{
                position: "relative",
                background: "var(--ink)",
                color: "var(--on-ink)",
                minHeight: "100vh",
                padding:
                    "clamp(120px, 14vw, 200px) clamp(24px, 5vw, 80px) clamp(80px, 10vw, 160px)",
            }}
            data-testid="page-product"
        >
            {/* TOP META */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "clamp(32px, 4vw, 56px)",
                }}
            >
                <MonoLabel
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <BlinkingDot />
                    Artifact [{product.n}]
                </MonoLabel>
                <MonoLabel
                    tone="mute"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <Link
                        to="/"
                        style={{ color: "inherit", textDecoration: "none" }}
                    >
                        Proof
                    </Link>
                    <span aria-hidden="true">/</span>
                    <Link
                        to="/shop"
                        style={{ color: "inherit", textDecoration: "none" }}
                    >
                        Lookbook
                    </Link>
                    <span aria-hidden="true">/</span>
                    <span style={{ color: "var(--on-ink-soft)" }}>
                        {product.name}
                    </span>
                </MonoLabel>
            </div>

            {/* MAIN: gallery + purchase */}
            <div className="proof-grid-pdp">
                {/* GALLERY */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                    }}
                    data-testid="product-gallery"
                >
                <div
                    style={{
                        aspectRatio: "4 / 5",
                        background: product.bgTone,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                        <span
                            style={{
                                position: "absolute",
                                top: 18,
                                left: 18,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.24em",
                                textTransform: "uppercase",
                                color: "rgba(12,12,12,0.6)",
                            }}
                        >
                            {product.drop}
                        </span>
                        <span
                            style={{
                                position: "absolute",
                                top: 18,
                                right: 18,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.24em",
                                textTransform: "uppercase",
                                color: "rgba(12,12,12,0.55)",
                            }}
                        >
                            {String(galleryIdx + 1).padStart(2, "0")} /{" "}
                            {String(product.gallery.length).padStart(2, "0")}
                        </span>
                        <motion.img
                            key={galleryIdx}
                            layoutId={
                                galleryIdx === 0
                                    ? `product-cover-${product.slug}`
                                    : undefined
                            }
                            initial={
                                galleryIdx === 0
                                    ? false
                                    : { opacity: 0, scale: 1.02 }
                            }
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, ease: easing.primary }}
                            src={product.gallery[galleryIdx]}
                            alt={product.name}
                            draggable={false}
                            data-testid="product-cover"
                            style={{
                                width: "78%",
                                height: "78%",
                                objectFit: "contain",
                                mixBlendMode: "multiply",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${product.gallery.length}, 1fr)`,
                            gap: 10,
                        }}
                    >
                        {product.gallery.map((src, i) => (
                            <button
                                key={src + i}
                                type="button"
                                onClick={() => setGalleryIdx(i)}
                                data-testid={`product-thumb-${i}`}
                                style={{
                                    aspectRatio: "1 / 1",
                                    background: product.bgTone,
                                    border:
                                        i === galleryIdx
                                            ? "1px solid #fff"
                                            : "1px solid rgba(255,255,255,0.15)",
                                    padding: 0,
                                    cursor: "pointer",
                                    transition: "border-color 0.3s",
                                    overflow: "hidden",
                                }}
                            >
                                <img
                                    src={src}
                                    alt=""
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        mixBlendMode: "multiply",
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                {/* PURCHASE COLUMN */}
                <div
                    style={{
                        position: "sticky",
                        top: 120,
                        display: "flex",
                        flexDirection: "column",
                        gap: 24,
                    }}
                    data-testid="product-buy"
                >
                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, ease: easing.primary }}
                        style={{
                            margin: 0,
                            fontFamily: "'Space Grotesk', Manrope, sans-serif",
                            fontWeight: 700,
                            fontSize: "clamp(36px, 4.4vw, 72px)",
                            letterSpacing: "-0.035em",
                            lineHeight: 0.95,
                            textTransform: "uppercase",
                        }}
                        data-testid="product-title"
                    >
                        {product.name}
                    </motion.h1>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 16,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 14,
                            letterSpacing: "0.18em",
                            color: "rgba(255,255,255,0.75)",
                        }}
                    >
                        <span data-testid="product-price">
                            {formatPrice(product.priceEUR)}
                        </span>
                        {product.badge && (
                            <span
                                style={{
                                    color: "#c1121f",
                                    fontSize: 10,
                                    letterSpacing: "0.28em",
                                }}
                            >
                                [ {product.badge} ]
                            </span>
                        )}
                    </div>

                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 16,
                            lineHeight: 1.55,
                            color: "rgba(255,255,255,0.78)",
                            maxWidth: "44ch",
                        }}
                        data-testid="product-description"
                    >
                        {product.fieldNotes}
                    </p>

                    {/* SIZE PICKER */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <span
                            style={{
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.55)",
                            }}
                        >
                            Size · {size || "Select"}
                        </span>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${product.sizes.length}, 1fr)`,
                                gap: 6,
                            }}
                        >
                            {product.sizes.map((s) => (
                                <motion.button
                                    key={s}
                                    type="button"
                                    onClick={() => setSize(s)}
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.94 }}
                                    transition={{ duration: 0.2, ease: easing.primary }}
                                    data-testid={`product-size-${s}`}
                                    style={{
                                        background:
                                            size === s ? "#fff" : "transparent",
                                        color: size === s ? "#0c0c0c" : "#fff",
                                        border:
                                            "1px solid rgba(255,255,255,0.5)",
                                        fontFamily:
                                            "'Space Grotesk', Manrope, sans-serif",
                                        fontWeight: 600,
                                        fontSize: 12,
                                        letterSpacing: "0.12em",
                                        padding: "10px 0",
                                        cursor: "pointer",
                                    }}
                                >
                                    {s}
                                </motion.button>
                            ))}
                        </div>
                    </div>

                    {/* CTAs */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                            marginTop: 10,
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={!size}
                            data-testid="product-add"
                            style={{
                                width: "100%",
                                background: size ? "#fff" : "rgba(255,255,255,0.25)",
                                color: size ? "#0c0c0c" : "rgba(255,255,255,0.55)",
                                border: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 12,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "16px 0",
                                cursor: size ? "pointer" : "not-allowed",
                                transition: "background 0.3s, color 0.3s",
                            }}
                        >
                            {added ? `Added · ${size}` : "Add to Cart"}
                        </button>
                        <button
                            type="button"
                            onClick={handleBuyNow}
                            disabled={!size}
                            data-testid="product-buynow"
                            style={{
                                width: "100%",
                                background: "transparent",
                                color: size ? "#fff" : "rgba(255,255,255,0.45)",
                                border: "1px solid rgba(255,255,255,0.45)",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 12,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "16px 0",
                                cursor: size ? "pointer" : "not-allowed",
                                transition: "all 0.3s",
                            }}
                        >
                            Buy Now
                        </button>
                    </div>

                    {/* META BLOCK */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                            paddingTop: 18,
                            borderTop: "1px solid rgba(255,255,255,0.14)",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.7)",
                        }}
                        data-testid="product-meta"
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <span style={{ color: "rgba(255,255,255,0.45)" }}>
                                Materials
                            </span>
                            <span
                                style={{
                                    textAlign: "right",
                                    color: "rgba(255,255,255,0.85)",
                                    textTransform: "none",
                                    letterSpacing: "0.02em",
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: 13,
                                }}
                            >
                                {product.materials}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <span style={{ color: "rgba(255,255,255,0.45)" }}>
                                Origin
                            </span>
                            <span
                                style={{
                                    textAlign: "right",
                                    color: "rgba(255,255,255,0.85)",
                                    textTransform: "none",
                                    letterSpacing: "0.02em",
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: 13,
                                }}
                            >
                                {product.origin}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <span style={{ color: "rgba(255,255,255,0.45)" }}>
                                Drop
                            </span>
                            <span style={{ color: "rgba(255,255,255,0.85)" }}>
                                {product.drop}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* PAIR WITH */}
            {pairWithItems.length > 0 && (
                <div
                    style={{
                        marginTop: "clamp(80px, 10vw, 140px)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 28,
                    }}
                    data-testid="product-pair-with"
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            paddingBottom: 14,
                            borderBottom: "1px solid rgba(255,255,255,0.14)",
                        }}
                    >
                        <h3
                            style={{
                                margin: 0,
                                fontFamily:
                                    "'Space Grotesk', Manrope, sans-serif",
                                fontWeight: 700,
                                fontSize: "clamp(24px, 3vw, 42px)",
                                letterSpacing: "-0.025em",
                                textTransform: "uppercase",
                            }}
                        >
                            Pair With
                        </h3>
                        <Link
                            to="/shop"
                            style={{
                                color: "rgba(255,255,255,0.65)",
                                textDecoration: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                            }}
                        >
                            See full Lookbook →
                        </Link>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fill, minmax(220px, 1fr))",
                            gap: 22,
                        }}
                    >
                        {pairWithItems.map((it) => (
                            <Link
                                key={it.n}
                                to={`/product/${it.slug}`}
                                data-testid={`pair-with-${it.slug}`}
                                style={{
                                    color: "inherit",
                                    textDecoration: "none",
                                    display: "block",
                                }}
                            >
                                <div
                                    style={{
                                        aspectRatio: "1 / 1",
                                        background: it.bgTone,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img
                                        src={it.cover}
                                        alt={it.name}
                                        style={{
                                            width: "82%",
                                            height: "82%",
                                            objectFit: "contain",
                                            mixBlendMode: "multiply",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        paddingTop: 12,
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily:
                                                "'Space Grotesk', sans-serif",
                                            fontSize: 14,
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {it.name}
                                    </span>
                                    <span
                                        style={{
                                            fontFamily:
                                                "JetBrains Mono, monospace",
                                            fontSize: 11,
                                            color: "rgba(255,255,255,0.6)",
                                        }}
                                    >
                                        {formatPrice(it.priceEUR)}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
